'use client';

/**
 * "Turn Timeline" — cross-feed view: one block per `turnId`, with that turn's
 * events listed in time order (RAG query → chunks → rerank → context injection
 * → LLM prompt → LLM completion → tool call → tool result → orchestration ...).
 * Events with no turnId fall into an "(unturned)" bucket. Newest turn first.
 *
 * Fed with ALL stream events (not a single feed) by AgentDebugDialog.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Copy } from 'lucide-react';
import type { AgentDebugEvent } from '@/lib/debug/types';
import { isMediaBlob, type MediaBlob as MediaBlobT } from '@/lib/debug/types';
import { MediaBlob } from './MediaBlob';

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

const SOURCE_COLOR: Record<string, string> = {
  'agent-caller': 'text-emerald-300',
  'officer-assist': 'text-sky-300',
  'agent-backend': 'text-amber-300',
  'ai-engine': 'text-fuchsia-300',
};

function payloadPreview(payload: unknown): string {
  if (payload == null) return '';
  if (typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    // Pick a useful field for a one-line preview.
    for (const k of ['query', 'text', 'message', 'label', 'tool_name', 'instructions', 'input']) {
      const v = p[k];
      if (typeof v === 'string' && v.trim()) return v.length > 160 ? v.slice(0, 160) + '…' : v;
    }
    try {
      const s = JSON.stringify(payload);
      return s.length > 160 ? s.slice(0, 160) + '…' : s;
    } catch {
      return '';
    }
  }
  const s = String(payload);
  return s.length > 160 ? s.slice(0, 160) + '…' : s;
}

function findBlobs(payload: unknown, acc: MediaBlobT[] = [], depth = 0): MediaBlobT[] {
  if (depth > 10 || payload == null || typeof payload !== 'object') return acc;
  if (isMediaBlob(payload)) {
    acc.push(payload);
    return acc;
  }
  if (Array.isArray(payload)) {
    for (const v of payload) findBlobs(v, acc, depth + 1);
    return acc;
  }
  for (const v of Object.values(payload as Record<string, unknown>)) findBlobs(v, acc, depth + 1);
  return acc;
}

function EventLine({ ev, callId }: { ev: AgentDebugEvent; callId: string | null }) {
  const [open, setOpen] = useState(false);
  const blobs = useMemo(() => findBlobs(ev.payload), [ev.payload]);
  const copy = () => {
    try {
      navigator.clipboard?.writeText(JSON.stringify(ev, null, 2));
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="border-b border-white/[0.05] py-0.5 pl-3 font-mono text-[11px]">
      <div className="flex items-center gap-2">
        <button onClick={() => setOpen((o) => !o)} className="text-slate-600 hover:text-slate-400">
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        <span className="text-slate-600">{fmtTime(ev.ts)}</span>
        <span className={SOURCE_COLOR[ev.source] ?? 'text-slate-400'}>{ev.source}</span>
        <span className="text-slate-500">{ev.feed}</span>
        <span className="text-sky-300">{ev.kind}</span>
        <span className="truncate text-slate-400">{payloadPreview(ev.payload)}</span>
        <button onClick={copy} className="ml-auto text-slate-600 hover:text-slate-400" title="Copy event JSON">
          <Copy className="h-3 w-3" />
        </button>
      </div>
      {open && (
        <div className="mt-0.5 max-h-[35vh] overflow-auto rounded bg-black/30 p-1">
          <pre className="whitespace-pre-wrap break-words text-[10px] text-emerald-300">{(() => {
            try {
              return JSON.stringify(ev.payload, null, 2);
            } catch {
              return String(ev.payload);
            }
          })()}</pre>
          {blobs.map((b, i) => (
            <MediaBlob key={i} blob={b} eventId={ev.id} callId={callId} />
          ))}
        </div>
      )}
    </div>
  );
}

export function TurnTimelineRenderer({ events, callId }: { events: AgentDebugEvent[]; callId: string | null; paused?: boolean }) {
  const [filter, setFilter] = useState('');
  const turns = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const filtered = f
      ? events.filter((e) => {
          try {
            return JSON.stringify(e).toLowerCase().includes(f);
          } catch {
            return false;
          }
        })
      : events;
    const byTurn = new Map<string, AgentDebugEvent[]>();
    for (const ev of filtered) {
      const key = ev.turnId || '(unturned)';
      const list = byTurn.get(key);
      if (list) list.push(ev);
      else byTurn.set(key, [ev]);
    }
    // Each turn's events sorted by ts; turns sorted by their max ts, newest first.
    const out = Array.from(byTurn.entries()).map(([turnId, evs]) => ({
      turnId,
      evs: [...evs].sort((a, b) => a.ts - b.ts),
      lastTs: Math.max(...evs.map((e) => e.ts)),
      firstTs: Math.min(...evs.map((e) => e.ts)),
    }));
    out.sort((a, b) => b.lastTs - a.lastTs);
    return out;
  }, [events, filter]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-white/[0.12] bg-[#0d0d0d] px-2 py-1.5 text-[11px]">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter turns"
          placeholder="filter…"
          className="w-40 rounded border border-white/[0.12] bg-[#0a0a0a] px-2 py-0.5 text-slate-200 placeholder:text-slate-600"
        />
        <span className="ml-auto text-slate-500">{turns.length} turn{turns.length === 1 ? '' : 's'} · {events.length} events</span>
      </div>
      <div className="flex-1 overflow-auto bg-[#0a0a0a]">
        {turns.length === 0 ? (
          <div className="p-3 text-[11px] text-slate-600">no events</div>
        ) : (
          turns.map((t) => <TurnBlock key={t.turnId} turnId={t.turnId} evs={t.evs} ms={t.lastTs - t.firstTs} callId={callId} />)
        )}
      </div>
    </div>
  );
}

function TurnBlock({ turnId, evs, ms, callId }: { turnId: string; evs: AgentDebugEvent[]; ms: number; callId: string | null }) {
  const [open, setOpen] = useState(true);
  // Per-source count summary.
  const summary = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of evs) m.set(e.source, (m.get(e.source) ?? 0) + 1);
    return Array.from(m.entries());
  }, [evs]);
  return (
    <div className="border-b border-white/[0.1]">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 bg-[#111] px-2 py-1 text-left text-[11px] font-mono text-slate-200 hover:bg-white/[0.04]"
      >
        {open ? <ChevronDown className="h-3 w-3 text-slate-500" /> : <ChevronRight className="h-3 w-3 text-slate-500" />}
        <span className="rounded bg-indigo-900/50 px-1 text-indigo-300">{turnId}</span>
        <span className="text-slate-500">{evs.length} events</span>
        {ms > 0 && <span className="text-slate-600">{ms} ms</span>}
        <span className="ml-auto flex gap-2 text-slate-600">
          {summary.map(([s, n]) => (
            <span key={s} className={SOURCE_COLOR[s] ?? 'text-slate-400'}>
              {s}:{n}
            </span>
          ))}
        </span>
      </button>
      {open && evs.map((ev) => <EventLine key={ev.id} ev={ev} callId={callId} />)}
    </div>
  );
}

export default TurnTimelineRenderer;
